import { SessionTimeoutModal } from "./SessionTimeoutModal/SessionTimeoutModal";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { InteractionStatus } from "@azure/msal-browser";

export function AutoLogout() {
  // const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const [isSessionTimeoutModalVisible, setSessionTimeoutModalVisible] =
    useState(false);

  const idleTimeout = 1000 * 60 * 20; //20 minutes
  const idleLogout = 1000 * 60 * 25; //25 Minutes (5 mins to extend after warning)
  const throttleDelay = 1000; // Throttle activity events to once per second
  
  const idleEventRef = useRef(null);
  const idleLogoutEventRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isModalVisibleRef = useRef(false); // Track modal state for event handlers

  // Comprehensive event list covering desktop and mobile interactions
  const events = [
    // Mouse events
    "mousemove", "click", "mousedown", "mouseup", "wheel",
    // Keyboard events
    "keypress", "keydown", "keyup",
    // Touch events (mobile/tablet)
    "touchstart", "touchmove", "touchend",
    // Scroll and navigation
    "scroll", "load",
    // Form interaction
    "input", "change", "focus", "focusin"
  ];

  /**
   * @method logOut
   * This function will destroy current user session.
   * Note: Only clears sessionStorage (tab-specific) to avoid affecting other tabs.
   * MSAL tokens are stored in sessionStorage, so other tabs retain their own sessions.
   */
  const logOut = useCallback(async () => {
    // Clear session storage (tab-specific - does not affect other tabs)
    sessionStorage.clear();

    // DO NOT clear localStorage - it's shared across tabs and would log out other tabs
    // localStorage.clear();

    // Use origin only (without /home path) - postLogoutRedirectUri must match exactly
    // what is registered in Azure AD App Registration's redirect URIs
    const postLogoutUri = window.location.origin;

    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      instance
        .logoutRedirect({
          account: instance.getActiveAccount(),
          postLogoutRedirectUri: postLogoutUri,
          onRedirectNavigate: () => {
            // Return false to prevent MSAL from navigating - we handle redirect ourselves
            // This prevents MSAL from clearing shared storage
            return true;
          },
        })
        .catch((e) => {
          sessionStorage.clear();
          // Clear MSAL interaction status cookie with secure attributes
          // Note: httpOnly cannot be set from client-side JavaScript
          document.cookie = "msal.interaction.status=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=Strict";
          instance.logoutRedirect({
            postLogoutRedirectUri: postLogoutUri,
          });
        });
    } else {
      // Fallback redirect if no active session
      window.location.href = postLogoutUri;
    }
  }, [instance, accounts, inProgress]);

  /**
   * @method sessionTimeout
   * This function is called with each event listener to set a timeout or clear a timeout.
   * Includes throttling to prevent excessive timer resets and checks if modal is visible.
   */
  const sessionTimeout = useCallback(() => {
    // Don't reset timers if modal is already visible - user must interact with modal
    if (isModalVisibleRef.current) {
      return;
    }

    // Throttle: only reset timers if enough time has passed since last activity
    const now = Date.now();
    if (now - lastActivityRef.current < throttleDelay) {
      return;
    }
    lastActivityRef.current = now;

    if (idleEventRef.current) clearTimeout(idleEventRef.current);
    if (idleLogoutEventRef.current) clearTimeout(idleLogoutEventRef.current);

    idleEventRef.current = setTimeout(
      () => setSessionTimeoutModalVisible(true),
      idleTimeout
    ); //show session warning modal.
    idleLogoutEventRef.current = setTimeout(() => logOut(), idleLogout); //Call logged out on session expire.
  }, [idleTimeout, idleLogout, logOut, throttleDelay]);

  /**
   * @method refreshToken
   * Silently refresh the access token to extend the session
   */
  const refreshToken = useCallback(async () => {
    if (inProgress === InteractionStatus.None && accounts.length > 0) {
      try {
        const request = {
          scopes: ["User.Read"],
          account: accounts[0],
          forceRefresh: true,
        };
        await instance.acquireTokenSilent(request);
        return true;
      } catch (error) {
        console.error("Token refresh failed:", error);
        return false;
      }
    }
    return false;
  }, [instance, accounts, inProgress]);

  /**
   * @method handleStaySignedIn
   * Handle stay signed in - refresh token and reset timers
   */
  const handleStaySignedIn = useCallback(async () => {
    isModalVisibleRef.current = false;
    setSessionTimeoutModalVisible(false);
    
    // Clear existing timers
    if (idleEventRef.current) clearTimeout(idleEventRef.current);
    if (idleLogoutEventRef.current) clearTimeout(idleLogoutEventRef.current);

    // Refresh token silently
    await refreshToken();

    // Reset last activity time and session timeout
    lastActivityRef.current = Date.now();
    sessionTimeout();
  }, [refreshToken, sessionTimeout]);

  /**
   * @method handleLogout
   * Handle logout from modal
   */
  const handleLogout = useCallback(() => {
    isModalVisibleRef.current = false;
    setSessionTimeoutModalVisible(false);
    logOut();
  }, [logOut]);

  // Keep modal ref in sync with state
  useEffect(() => {
    isModalVisibleRef.current = isSessionTimeoutModalVisible;
  }, [isSessionTimeoutModalVisible]);

  /**
   * Handle visibility change - pause/resume idle tracking when tab is hidden/visible
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && !isModalVisibleRef.current) {
      // Tab became visible - reset activity timer
      lastActivityRef.current = Date.now();
      sessionTimeout();
    }
  }, [sessionTimeout]);

  useEffect(() => {
    // Add activity event listeners
    for (let e in events) {
      window.addEventListener(events[e], sessionTimeout);
    }
    
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Remove activity event listeners
      for (let e in events) {
        window.removeEventListener(events[e], sessionTimeout);
      }
      // Remove visibility change listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Clear timers on unmount
      if (idleEventRef.current) clearTimeout(idleEventRef.current);
      if (idleLogoutEventRef.current) clearTimeout(idleLogoutEventRef.current);
    };
  }, [sessionTimeout, handleVisibilityChange]);


  useEffect(() => {
    // Prevent back navigation
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, null, window.location.href);
    };
  }, []);

  return (
    <SessionTimeoutModal
      isModalVisible={isSessionTimeoutModalVisible}
      onCancel={handleLogout}
      onConfirm={handleStaySignedIn}
    />
  );
}
